import { formatSubmissionListDate } from "@/shared/utils/format.js";
import { fmtDate } from "@/shared/utils/dateHelpers.js";
import {
  deriveHoursFactorFromIlf,
  deriveHoursWorkedFromIlf,
} from "./ilfHelpers.js";
import { buildSignupQuestionSaveRequest } from "@/modules/Quote/api/questionsApi.js";

/**
 * Build the `/insured/submission` (or signup-wrapped) request body from the
 * quote inputs + ILF/DLF response + the user's classification answers.
 * Used by ClassificationPage (authed flow) and RegistrationPage (signup flow).
 *
 * `visitedQuestionGroups` is the list of group names the user actually
 * reached during the anonymous flow — only those answers are attached, so
 * we never save groups the user didn't see.
 */
export function buildSubmissionRequest({
  zip, effDate, ilfDlfResponse,
  visitedQuestionGroups, questionGroupsWithIds,
  questionAnswers, impactAnswers, hiddenQuestionIds,
}) {
  const visitedNames = new Set(
    (visitedQuestionGroups || []).map((n) => String(n).trim().toLowerCase()),
  );
  const visitedGroups = (questionGroupsWithIds || []).filter((g) =>
    visitedNames.has(String(g?.groupName || "").trim().toLowerCase()),
  );
  const questionSaveRequest = buildSignupQuestionSaveRequest(
    visitedGroups, questionAnswers, impactAnswers, hiddenQuestionIds,
  );
  return {
    zipcode: zip,
    effectiveDate: fmtDate(effDate),
    coverageilfdlfid: Number(ilfDlfResponse.defaultIlfDlf),
    hoursperweek: deriveHoursWorkedFromIlf(ilfDlfResponse),
    parttimeFulltimeFactor: deriveHoursFactorFromIlf(ilfDlfResponse),
    year: Number(ilfDlfResponse.defaultyear),
    speciality: Number(ilfDlfResponse.defaultSurgery),
    claims: Number(ilfDlfResponse.defaultClaims || 0),
    ...(questionSaveRequest ? { questionSaveRequest } : {}),
  };
}

export function parsePositiveSubmissionId(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Submission id from signup / InsuredAuthToken response. */
export function submissionIdFromSignupResponse(resp) {
  if (!resp || typeof resp !== "object") return null;
  const direct = parsePositiveSubmissionId(resp.submissionId ?? resp.submissionid);
  if (direct) return direct;
  const o0 = Array.isArray(resp.openOrders) ? resp.openOrders[0] : null;
  return parsePositiveSubmissionId(o0?.submissionid);
}

/** Submission id from POST /insured/submission (OpenOrderDetailsDto). */
export function submissionIdFromOpenOrderDto(dto) {
  if (!dto || typeof dto !== "object") return null;
  return parsePositiveSubmissionId(dto.submission ?? dto.submissionId);
}

export function isInsuredSessionPayload(data) {
  if (data == null || typeof data !== "object") return false;
  return data.id != null || data.username != null || data.name != null;
}

export function primaryLocationFromList(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.find((l) => l?.isprimary) || list[0];
}

export function googleDataFromResponse(payload) {
  if (!payload || typeof payload !== "object") return { firstname: "", lastname: "", email: "" };
  const firstname = payload.firstname ?? payload.firstName ?? payload.first_name;
  const lastname = payload.lastname ?? payload.lastName ?? payload.last_name;
  const email = payload.email ?? payload.username;
  return {
    firstname: firstname != null ? String(firstname) : "",
    lastname: lastname != null ? String(lastname) : "",
    email: email != null ? String(email) : "",
  };
}

/**
 * Policy documents (COI, Binder, full policy) may only be downloaded once a
 * policy has actually been issued — i.e. its workflow status resolves to
 * "Policy Active" (in force / endorsement / processing) or "Policy Inactive"
 * (cancelled / expired / declined). Every earlier status — Open, Priced, Pay,
 * Pending Review, Binding Policy, etc. — blocks download.
 */
export function canDownloadPolicyDocuments(s) {
  const status = (
    s?.statusname || s?.policystatus || s?.filterstatus || s?.workFlowStatus || s?.workflowstatus || ""
  ).toString().trim().toLowerCase();
  return status === "policy active" || status === "policy inactive";
}

export function mapSubmissionToPolicyCard(s) {
  const num = s.policynumber || s.quotenumber || (s.submissionid != null ? `#${s.submissionid}` : "—");
  const statusLabel = s.statusname || s.policystatus || (s.ispolicyactive ? "Active" : "Inactive");
  const eff = formatSubmissionListDate(s.effectivedate);
  const exp = formatSubmissionListDate(s.expireddate);
  // Retroactive date — show the formatted date when present, else "N/A".
  // A retro date equal to the effective date means "no prior acts" (a new
  // claims-made policy), so we show "N/A" in that case rather than echoing
  // the effective date.
  const rawRetro = s.retrodate ?? s.retroDate ?? s.retroactivedate;
  const retroFmt = rawRetro ? formatSubmissionListDate(rawRetro) : "";
  const retro = (retroFmt && retroFmt !== eff) ? retroFmt : "N/A";
  const prem = s.balance != null && s.balance !== "" ? `$${Number(s.balance).toLocaleString()}` : "—";
  const zipState = s.practicezipcode || "—";
  const lim = s.speciality || "—";
  const isActive = Boolean(s.ispolicyactive);
  return { num, statusLabel, eff, exp, retro, prem, zipState, lim, isActive };
}
